/**
 * WeblabsReportContent — the Weblab Visual Dashboard body, rendered as the
 * `weblabs-visual` plugin's ReportContent inside DashboardShell (so it appears
 * as a sub-item under the "Dashboard" selector, alongside Weblab Tracker).
 *
 * Self-contained (like the Weblab Tracker plugin): it owns the full filter set
 * and calls useWeblabsData, which runs small SERVER-SIDE aggregate queries (no
 * raw-row pull, so nothing truncates for long ranges / many weblabs). Date
 * range + marketplace + SP/SB + activation-state / weblab-type / start-launch
 * range are committed together on Apply; grouping re-buckets instantly. The
 * PluginReportContentProps from the shell are intentionally ignored — this
 * dashboard manages its own data (the plugin's useData is a stub).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import type { PluginReportContentProps } from "../../types";
import WeblabsFilterPanel from "./WeblabsFilterPanel";
import WeblabsContent, { type WeblabsDraftFilters } from "./WeblabsContent";
import { useWeblabsData } from "./useWeblabsData";
import type { Grouping } from "./weblabsChart";
import { buildWeblabsShareUrl, parseWeblabsShareUrl } from "./weblabsShare";

/** Format a Date as YYYY-MM-DD (local). */
function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** The full Apply-gated filter set (dates + marketplace + toolbar filters). */
interface WeblabsFilterState extends WeblabsDraftFilters {
  startDate: string;
  endDate: string;
  country: string;
}

/**
 * Default filters. Date range = the two most recent COMPLETE calendar weeks
 * (Sun–Sat): the prior week's Saturday as the end, the Sunday 13 days before as
 * the start. Everything else starts unset (All) with SP selected.
 */
function defaultFilters(): WeblabsFilterState {
  const end = new Date();
  end.setDate(end.getDate() - (end.getDay() + 1));
  const start = new Date(end);
  start.setDate(start.getDate() - 13);
  return {
    startDate: toIso(start),
    endDate: toIso(end),
    country: "",
    metric: "impact_sp_rev_day",
    activationState: "",
    weblabType: "",
    runStart: "",
    runEnd: "",
  };
}

/** Initial filters + grouping — a shared-link's params (if present) override
 *  the defaults so the recipient lands on the exact shared view. */
function initialState(): { filters: WeblabsFilterState; grouping: Grouping } {
  const base = defaultFilters();
  const s = parseWeblabsShareUrl();
  if (!s) return { filters: base, grouping: "D" };
  const filters: WeblabsFilterState = {
    startDate: s.startDate ?? base.startDate,
    endDate: s.endDate ?? base.endDate,
    country: s.country ?? base.country,
    metric: s.metric ?? base.metric,
    activationState: s.activationState ?? base.activationState,
    weblabType: s.weblabType ?? base.weblabType,
    runStart: s.runStart ?? base.runStart,
    runEnd: s.runEnd ?? base.runEnd,
  };
  return { filters, grouping: s.grouping ?? "D" };
}

const WeblabsReportContent: React.FC<PluginReportContentProps> = ({
  registerShareBuilder,
}) => {
  const initial = useRef(initialState());
  // Draft = what the user is editing; applied = what the queries use. ALL data
  // filters commit together on Apply. Grouping is client-side (instant). A
  // shared link seeds draft AND applied so it loads the shared view on mount.
  const [draft, setDraft] = useState<WeblabsFilterState>(
    initial.current.filters
  );
  const [applied, setApplied] = useState<WeblabsFilterState>(
    initial.current.filters
  );
  const [grouping, setGrouping] = useState<Grouping>(initial.current.grouping);

  const params = useMemo(
    () => ({
      startDate: applied.startDate,
      endDate: applied.endDate,
      country: applied.country,
      activationState: applied.activationState,
      weblabType: applied.weblabType,
      runStart: applied.runStart,
      runEnd: applied.runEnd,
      metric: applied.metric,
    }),
    [applied]
  );

  const { data, isLoading, error, fetchBreakdown, fetchDrill } =
    useWeblabsData(params);

  const updateDraft = useCallback(
    (partial: Partial<WeblabsDraftFilters>) =>
      setDraft((d) => ({ ...d, ...partial })),
    []
  );
  const applyAll = useCallback(() => setApplied({ ...draft }), [draft]);

  // Register a Share-URL builder so the shell header's Share button captures
  // THIS dashboard's applied filters + grouping (the shell can't see them).
  useEffect(() => {
    if (!registerShareBuilder) return;
    registerShareBuilder(() =>
      buildWeblabsShareUrl({
        startDate: applied.startDate,
        endDate: applied.endDate,
        country: applied.country,
        metric: applied.metric,
        activationState: applied.activationState,
        weblabType: applied.weblabType,
        runStart: applied.runStart,
        runEnd: applied.runEnd,
        grouping,
      })
    );
    return () => registerShareBuilder(null);
  }, [registerShareBuilder, applied, grouping]);

  return (
    <Box>
      <WeblabsFilterPanel
        filters={draft}
        onFiltersChange={(f) => setDraft(f as WeblabsFilterState)}
        onApply={applyAll}
        isLoading={isLoading}
        grouping={grouping}
        onGroupingChange={setGrouping}
        countryOptions={data?.options.countries ?? []}
      />
      <WeblabsContent
        data={data}
        isLoading={isLoading}
        error={error}
        grouping={grouping}
        appliedMetric={applied.metric}
        draft={draft}
        onDraftChange={updateDraft}
        fetchBreakdown={fetchBreakdown}
        fetchDrill={fetchDrill}
      />
    </Box>
  );
};

export default WeblabsReportContent;
